import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router } from '@angular/router';

/**
 * Backs the public-share alias route `/campaigns/:campaignId`.
 *
 * The route renders `CampaignShareViewPage` (a rich, marketing-style layout
 * mirroring the operator's preview blocks). As of the campaign-preview
 * unification work, this is the canonical public-share surface — both
 * authenticated creators AND unauthenticated visitors land here. The edge
 * 301 from `www.anothertrip.com/campaigns/{id}` points to this URL.
 *
 * Behaviour:
 *  - Both auth states render the page. The page itself detects auth via
 *    `ConfigurableAuthService.isLoggedIn()` to decide which endpoint to call
 *    (authed marketplace endpoint vs unauthed public endpoint) and which
 *    apply-CTA mode to expose (`creator-alias` vs `consumer-public`).
 *  - The Apply CTA in `consumer-public` mode triggers the auth round-trip
 *    when clicked, stashing the campaign URL via `NavigationContextService`.
 *    That logic lives in the page, not here, so visitors who never click
 *    Apply never get bounced through `/login`.
 *
 * Distinct from the compact `CampaignDetailsPage` used inside the dashboard at
 * `/dashboard/marketplace/campaign/:id` — that split keeps the public-share
 * URL alive on staging/prod even while the broader marketplace feature is
 * still gated behind a splash page.
 *
 * The only thing this guard does today is reject malformed paths.
 */
export const publicCampaignAliasGuard: CanActivateFn = (route) => {
  const campaignId = route.paramMap.get('campaignId');
  if (!campaignId) {
    return inject(Router).createUrlTree(['/page-not-found']);
  }
  return true;
};

/**
 * `/campaigns/:campaignId` is a top-level route evaluated before the dashboard layout, so on its own it
 * would swallow `/campaigns/mine` as a campaign id of "mine" — and `canActivate` cannot fall through to
 * a later route. This `canMatch` refuses the segments that belong to app routes, which lets the router
 * carry on to them.
 */
export const RESERVED_CAMPAIGN_SEGMENTS: ReadonlySet<string> = new Set(['mine']);

export const publicCampaignAliasMatch: CanMatchFn = (_route, segments) =>
  !RESERVED_CAMPAIGN_SEGMENTS.has(segments[1]?.path ?? '');
